import { ReactNode } from "react";
import { Modal } from "@carbon/react";
import i18next from "../../i18n";
import { FileReader } from "..";
import { OfferResponse } from "../../types";
import {
  currencyFormatter,
  linePriceInputsOf,
  offerLineTotals,
  pricedLinesOf,
} from "../../utils";
import LinePriceTable, { formatTotals } from "../LinePriceTable/LinePriceTable";

type AwardOfferDetailProps = {
  open: boolean;
  onClose: () => void;
  offer: OfferResponse | null;
  /** Single-award uses `toLocaleDateString`, joint uses `formatDate`; passed in to preserve each. */
  dateFormatter: (value: string) => string;
};

const label: React.CSSProperties = {
  textTransform: "uppercase",
  fontWeight: 600,
  fontSize: "0.75rem",
  color: "var(--cds-text-primary, #161616)",
  margin: 0,
};

const value: React.CSSProperties = {
  fontSize: "0.75rem",
  color: "var(--cds-text-secondary, #525252)",
  margin: "0.25rem 0 0",
  whiteSpace: "pre-wrap",
};

const Field = ({ title, children }: { title: string; children: ReactNode }) => (
  <div style={{ marginBottom: "0.75rem" }}>
    <p style={label}>{title}</p>
    <div style={value}>{children}</div>
  </div>
);

/**
 * Read-only offer detail, opened from the comparison matrix's eye action. Replaces the old
 * Tailwind `Slide` with Carbon's passive `Modal` (Carbon has no side-panel primitive in this
 * version). Shared by both the single and joint award panels.
 */
const AwardOfferDetail = ({
  open,
  onClose,
  offer,
  dateFormatter,
}: AwardOfferDetailProps) => (
  <Modal
    open={open}
    passiveModal
    modalHeading={i18next.t("award.offerDetails")}
    onRequestClose={onClose}
  >
    {offer && (
      <div>
        <Field title={i18next.t("offer.seller")}>
          {offer.user?.company?.name} - {offer.user?.company?.taxId}
          <br />
          {offer.user?.fullName}
          <br />
          {offer.user?.email}
          <br />
          {offer.user?.phone}
        </Field>

        <Field title={i18next.t("award.price")}>
          {offerLineTotals(offer).length
            ? formatTotals(offerLineTotals(offer))
            : `${currencyFormatter(offer.price, offer.currency)} / ${
                offer.order?.measurementUnit
              }`}
        </Field>

        {/* SCRUM-116: what the supplier charges for each line of the service. */}
        {(offer.linePrices?.length ?? 0) > 0 &&
          pricedLinesOf(offer.order).length > 0 && (
            <Field title={i18next.t("linePricing.breakdown")}>
              <LinePriceTable
                lines={pricedLinesOf(offer.order)}
                prices={linePriceInputsOf(
                  pricedLinesOf(offer.order),
                  offer.linePrices,
                  offer.currency
                )}
                showDetail={false}
              />
            </Field>
          )}

        {offer.paymentMethod && (
          <Field title={i18next.t("offer.paymentMethod")}>
            {offer.paymentMethod}
            {offer.paymentTerm &&
              ` ${offer.paymentTerm} ${i18next.t("analytics.days")}`}
          </Field>
        )}

        {offer.leadTime !== undefined && (
          <Field title={i18next.t("award.leadTime")}>
            {offer.leadTime} {i18next.t("analytics.days")}
          </Field>
        )}

        {offer.offerValidity && (
          <Field title={i18next.t("award.offerValidity")}>
            {dateFormatter(offer.offerValidity)}
          </Field>
        )}

        {offer.expireDate && (
          <Field title={i18next.t("award.expireDate")}>
            {dateFormatter(offer.expireDate)}
          </Field>
        )}

        {offer.deliveryCost !== undefined && (
          <Field title={i18next.t("award.deliveryCost")}>
            {offer.deliveryCost === 0
              ? i18next.t("offer.deliveryIncludedBuyer")
              : offer.deliveryPlace === "BUYER"
                ? i18next.t("offer.deliveryCostBuyer")
                : currencyFormatter(
                    offer.deliveryCost,
                    offer.deliveryCostCurrency || offer.currency
                  )}
          </Field>
        )}

        {offer.pickupAddress && (
          <Field title={i18next.t("offer.pickupAddress")}>
            {offer.pickupAddress}
          </Field>
        )}

        {offer.comment && (
          <Field title={i18next.t("award.comment")}>{offer.comment}</Field>
        )}

        {offer.fileURL && (
          <Field title={i18next.t("award.fileUrl")}>
            <FileReader fileKey={offer.fileURL} />
          </Field>
        )}
      </div>
    )}
  </Modal>
);

export default AwardOfferDetail;
